import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchIssueHistory, type SiteIssueChange } from "../../api/siteIssuesApi";
import { Modal, ModalBody, ModalHeader } from "../../components/ui/Modal";
import { FIELD_LABELS, fieldValue } from "./siteIssueRows";

const ACTION_LABEL: Record<SiteIssueChange["action"], string> = {
  Create: "Dodano",
  Update: "Zmieniono",
  Delete: "Usunięto",
  Migrate: "Przeniesiono z raportu",
};

const when = (iso: string) =>
  new Date(iso.endsWith("Z") ? iso : iso + "Z").toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

// Historia pozycji rejestru: każda zmiana z autorem, czasem i miejscem (raport albo rejestr ośrodka)
export const SiteIssueHistoryModal = ({
  issueId, title, onClose, categoryName,
}: { issueId: string; title: string; onClose: () => void; categoryName: (id: number) => string }) => {
  const { data, isLoading, isError } = useQuery(["siteIssueHistory", issueId], () => fetchIssueHistory(issueId));
  const changes = [...(data ?? [])].reverse();

  return (
    <Modal open onClose={onClose} size="xl">
      <ModalHeader title={`Historia: ${title}`} onClose={onClose} />
      <ModalBody>
        {isLoading && <p className="text-sm text-[var(--sc-muted)]">Ładowanie…</p>}
        {isError && <p className="text-sm text-[var(--sc-red)]">Nie udało się pobrać historii.</p>}
        <ol className="space-y-3">
          {changes.map((c) => {
            const first = c.changedFields.length === 0;
            return (
              <li key={c.id} className="border border-[var(--sc-border)] rounded-lg px-4 py-3">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                  <span className="font-semibold text-[var(--sc-text)]">
                    {c.action === "Migrate" && !first ? "Zmiana w raporcie" : ACTION_LABEL[c.action]}
                  </span>
                  <span className="text-[var(--sc-muted)]">{when(c.changedAt)}</span>
                  <span className="text-[var(--sc-muted)]">· {c.changedBy || "—"}</span>
                  <span className="text-[var(--sc-muted)]">
                    · {c.reportId
                      ? <Link to={`/reports/${c.reportId}`} className="text-[var(--sc-blue)] hover:underline">{c.reportName ?? "raport"}</Link>
                      : "rejestr ośrodka"}
                  </span>
                </div>
                {first && c.state && c.action !== "Delete" && (
                  <p className="mt-1.5 text-[13px] text-[var(--sc-text)] whitespace-pre-wrap">{c.state.description}</p>
                )}
                {!first && (
                  <dl className="mt-1.5 grid grid-cols-[180px_1fr] gap-x-3 gap-y-1 text-[13px]">
                    {c.changedFields.map((f) => (
                      <div key={f} className="contents">
                        <dt className="text-[var(--sc-muted)]">{FIELD_LABELS[f] ?? f}</dt>
                        <dd className="text-[var(--sc-text)] whitespace-pre-wrap break-words">{fieldValue(c.state, f, categoryName)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </li>
            );
          })}
        </ol>
      </ModalBody>
    </Modal>
  );
};
